/**
 * Simple Cart Manager
 * Handles cart drawer interactions: quantity changes, remove items, refresh display
 * 
 * @requires ThemeUtils (utils.js) - optional, uses fallbacks if not available
 */

class SimpleCartManager {
  constructor() {
    this.cart = null;
    this.isUpdating = false;
    this.init();
  }

  init() {
    // Event delegation for cart buttons
    document.addEventListener('click', this.handleClick.bind(this));
    
    // Listen when products are added
    document.addEventListener('product:added', (event) => {
      const item = event.detail?.item;
      if (item) this.injectPreviewItem(item);
      this.loadCartData(true);
    });

    // Listen when drawer opens
    document.addEventListener('openDrawer', (event) => {
      if (event.detail?.drawerId === 'cart') {
        this.loadCartData();
      }
    });

    // Load initial data
    this.loadCartData();
  }

  handleClick(event) {
    // Handle quantity buttons
    const quantityButton = event.target.closest('.quantity-button');
    if (quantityButton) {
      event.preventDefault();
      event.stopPropagation();
      this.handleQuantityChange(quantityButton);
      return;
    }

    // Handle remove buttons
    const removeButton = event.target.closest('.cart-item-remove');
    if (removeButton) {
      event.preventDefault();
      event.stopPropagation();
      this.handleRemoveItem(removeButton);
      return;
    }
  }

  async handleQuantityChange(button) {
    if (this.isUpdating) return;

    const variantId = button.dataset.variantId;
    const action = button.dataset.action;
    const quantityElement = button.parentElement?.querySelector('.quantity-value');
    
    if (!variantId || !quantityElement) return;

    const currentQuantity = parseInt(quantityElement.dataset.quantity) || parseInt(quantityElement.textContent);
    let newQuantity = currentQuantity;

    if (action === 'increase') {
      newQuantity = currentQuantity + 1;
    } else if (action === 'decrease') {
      newQuantity = Math.max(0, currentQuantity - 1);
    }

    await this.updateCartItem(variantId, newQuantity);
  }

  async handleRemoveItem(button) {
    if (this.isUpdating) return;
    
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    await this.updateCartItem(variantId, 0);
  }

  async updateCartItem(variantId, quantity) {
    if (this.isUpdating) return;
    
    this.isUpdating = true;
    this.setButtonsLoading(variantId, true);

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity })
      });

      const result = await response.json();

      if (response.ok || response.status === 422) {
        // 422 means stock limit, but still update display
        await this.loadCartData();
        
        if (response.status === 422) {
          this.showMessage(result.message || 'Stock limited - quantity adjusted');
        }
      } else {
        this.showMessage('Error updating cart');
      }
    } catch (error) {
      this.showMessage('Connection error');
    } finally {
      this.setButtonsLoading(variantId, false);
      this.isUpdating = false;
    }
  }

  async loadCartData(openCart = false) {
    try {
      const response = await fetch('/cart.js');
      this.cart = await response.json();
      
      // If opening cart (new product), reload full HTML
      if (openCart) {
        await this.refreshCartDrawer();
      } else {
        this.updateCartDisplay();
      }
      
      this.updateCartCount();

      if (openCart) {
        this.openCart();
      }
    } catch (error) {
      // Silent fail - cart will be loaded on next attempt
    }
  }

  async refreshCartDrawer() {
    const cartItemsContainer = document.querySelector('.cart-items');
    if (!cartItemsContainer) return;

    try {
      // Get updated cart snippet - use locale-aware URL so Liquid renders translated product titles
      const cartUrl = window.theme?.routes?.cart || '/cart';
      const response = await fetch(cartUrl, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      
      if (response.ok) {
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        // Extract only cart-items content
        const newCartItems = doc.querySelector('.cart-items');
        if (newCartItems) {
          cartItemsContainer.innerHTML = newCartItems.innerHTML;
        }
      }
    } catch (error) {
      // Fallback to normal method
      this.updateCartDisplay();
    }
  }

  updateCartDisplay() {
    if (!this.cart) return;

    const cartEmpty = document.getElementById('cart-empty');
    const cartContent = document.getElementById('cart-content');
    const cartSubtotal = document.getElementById('cart-subtotal');

    // Show/hide empty state
    if (this.cart.item_count === 0) {
      if (cartEmpty) cartEmpty.style.display = 'flex';
      if (cartContent) cartContent.style.display = 'none';
    } else {
      if (cartEmpty) cartEmpty.style.display = 'none';
      if (cartContent) cartContent.style.display = 'block';
      
      // Update subtotal
      if (cartSubtotal) {
        cartSubtotal.textContent = this.formatMoney(this.cart.total_price);
      }

      // Update each existing item
      this.cart.items.forEach(item => {
        const variantId = item.variant_id;
        const itemElement = document.querySelector(`[data-cart-item="${variantId}"]`);
        
        if (itemElement) {
          this.updateExistingCartItem(itemElement, item);
        }
      });

      // Remove items that no longer exist
      const allCartItems = document.querySelectorAll('.cart-item[data-cart-item]');
      allCartItems.forEach(itemElement => {
        const variantId = itemElement.dataset.cartItem;
        const itemExists = this.cart.items.some(item => item.variant_id.toString() === variantId);
        
        if (!itemExists) {
          itemElement.remove();
        }
      });
    }
  }

  updateExistingCartItem(itemElement, item) {
    // Update quantity
    const quantityElement = itemElement.querySelector('.quantity-value');
    if (quantityElement) {
      quantityElement.textContent = item.quantity;
      quantityElement.dataset.quantity = item.quantity;
    }
    
    // Update price
    const priceElement = itemElement.querySelector('.cart-item-price-final');
    const priceContainer = itemElement.querySelector('.cart-item-price');
    if (priceElement) {
      priceElement.textContent = this.formatMoney(item.final_line_price);
    }
    
    // Detect discount and apply has-sale class
    if (priceContainer) {
      const hasDiscount = item.original_line_price && item.original_line_price > item.final_line_price;
      if (hasDiscount) {
        priceContainer.classList.add('has-sale');
        // Update original price if exists
        const originalPriceElement = itemElement.querySelector('.cart-item-price-original');
        if (originalPriceElement) {
          originalPriceElement.textContent = this.formatMoney(item.original_line_price);
        }
      } else {
        priceContainer.classList.remove('has-sale');
      }
    }
  }

  updateCartCount() {
    const cartCountElements = document.querySelectorAll('.cart-count');
    const itemCount = this.cart?.item_count || 0;
    
    cartCountElements.forEach(element => {
      element.textContent = itemCount;
      element.style.display = itemCount > 0 ? '' : 'none';
    });

    document.querySelectorAll('.cart-count-wrapper').forEach(el => {
      el.style.display = itemCount > 0 ? '' : 'none';
    });
  }

  setButtonsLoading(variantId, loading) {
    const itemElement = document.querySelector(`[data-cart-item="${variantId}"]`);
    if (!itemElement) return;

    const buttons = itemElement.querySelectorAll('.quantity-button, .cart-item-remove');
    buttons.forEach(button => {
      button.disabled = loading;
      button.classList.toggle('loading', loading);
    });
  }

  openCart() {
    // Dispatch openDrawer event (handled by drawer.js)
    document.dispatchEvent(new CustomEvent('openDrawer', { 
      detail: { drawerId: 'cart', position: 'right' } 
    }));
  }

  /**
   * Format money value
   */
  formatMoney(cents) {
    // Use ThemeUtils if available
    if (window.ThemeUtils?.formatMoney) {
      return window.ThemeUtils.formatMoney(cents);
    }
    // Legacy fallback
    if (window.formatMoney) {
      return window.formatMoney(cents);
    }
    return `$${(cents / 100).toFixed(2)}`;
  }

  /**
   * Immediately inject a preview cart item using data from /cart/add.js
   * This runs before the full refreshCartDrawer() fetch, so the image appears instantly
   */
  injectPreviewItem(item) {
    const cartItems = document.getElementById('cart-items');
    const cartContent = document.getElementById('cart-content');
    const cartEmpty = document.getElementById('cart-empty');

    if (!cartItems) return;

    // Show cart content and hide empty state right away
    if (cartEmpty) cartEmpty.style.display = 'none';
    if (cartContent) cartContent.style.display = 'block';

    // If this variant is already in the drawer, skip — the full refresh will update it
    if (cartItems.querySelector(`[data-cart-item="${item.variant_id}"]`)) return;

    // Build a correctly-sized Shopify CDN URL (append _240x before the extension)
    const rawUrl = item.featured_image?.url || '';
    const imageUrl = rawUrl
      ? rawUrl.replace(/(\.[a-z]+)(\?|$)/i, '_240x$1$2')
      : null;

    const title = item.product_title || item.title || '';
    const price = this.formatMoney(item.final_line_price || item.price);

    const previewEl = document.createElement('div');
    previewEl.className = 'cart-item';
    previewEl.dataset.cartItem = item.variant_id;
    if (item.product_id) previewEl.dataset.productId = item.product_id;
    previewEl.innerHTML = imageUrl
      ? `<div class="cart-item-image">
           <div class="cart-image-spinner"></div>
           <img src="${imageUrl}" alt="${title.replace(/"/g, '&quot;')}" width="170" height="227" loading="eager">
         </div>
         <div class="cart-item-details button-text">
           <div class="cart-item-info">
             <h4 class="cart-item-title heading-lg">${title}</h4>
             <div class="cart-item-price">
               <span class="cart-item-price-final">${price}</span>
             </div>
           </div>
         </div>`
      : `<div class="cart-item-image"><div class="cart-item-placeholder"><span>IMG</span></div></div>
         <div class="cart-item-details button-text">
           <div class="cart-item-info">
             <h4 class="cart-item-title heading-lg">${title}</h4>
           </div>
         </div>`;

    // Wire up the image spinner (add .loaded class on load to trigger CSS fade-in)
    if (imageUrl) {
      const img = previewEl.querySelector('img');
      img.addEventListener('load', () => img.classList.add('loaded'));
      img.addEventListener('error', () => {
        const spinner = previewEl.querySelector('.cart-image-spinner');
        if (spinner) spinner.style.display = 'none';
      });
    }

    // Keep the instant preview with the line items, above recommendations and the spacer.
    const anchor = cartItems.querySelector('[data-pp-upsell]') || cartItems.querySelector('.cart-spacer');
    if (anchor) {
      cartItems.insertBefore(previewEl, anchor);
    } else {
      cartItems.prepend(previewEl);
    }
  }

  /**
   * Show temporary message to user
   */
  showMessage(message) {
    const messageDiv = document.createElement('div');
    messageDiv.textContent = message;
    messageDiv.className = 'cart-notification';
    messageDiv.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #333;
      color: white;
      padding: 12px 16px;
      border-radius: 4px;
      z-index: 10000;
      font-size: 14px;
      max-width: 300px;
    `;
    
    document.body.appendChild(messageDiv);
    
    setTimeout(() => {
      messageDiv.remove();
    }, 3000);
  }
}

// Initialize only if no other cart manager exists
document.addEventListener('DOMContentLoaded', () => {
  if (!window.unifiedCart && !window.cartManager) {
    window.cartManager = new SimpleCartManager();
  }
});